"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

const CreateBot = dynamic(() => import("@/components/CreateBot/CreateBot"), {
  ssr: false,
  loading: () => <CreateBotPlaceholder />,
});

function CreateBotPlaceholder() {
  return (
    <div
      className="min-h-[520px] animate-pulse rounded-2xl border border-white/10 bg-white/[0.03] sm:min-h-[560px]"
      aria-busy="true"
      aria-label="Loading volume bot configuration tool"
      role="status"
    />
  );
}

function shouldLoadFromHash() {
  if (typeof window === "undefined") return false;
  return window.location.hash.includes("create-bot");
}

/** Defers CreateBot JS until scroll, deep-link hash, or first user interaction. */
export default function VolumeBotCreateBotLazy({
  afterEngines,
  afterStep2,
  ...props
}) {
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  /** Once CreateBot has mounted, never swap back to placeholder (preserves form state). */
  const createBotEverMountedRef = useRef(false);

  useEffect(() => {
    if (shouldLoad) return;

    let cancelled = false;
    const trigger = () => {
      if (!cancelled) setShouldLoad(true);
    };

    if (shouldLoadFromHash()) {
      trigger();
      return;
    }

    const onHashChange = () => {
      if (shouldLoadFromHash()) trigger();
    };
    window.addEventListener("hashchange", onHashChange);

    const onFirstInteraction = () => trigger();
    document.addEventListener("pointerdown", onFirstInteraction, {
      passive: true,
      once: true,
    });

    const el = containerRef.current;
    const observer =
      el && typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            ([entry]) => {
              if (entry?.isIntersecting) {
                trigger();
                observer.disconnect();
              }
            },
            { rootMargin: "320px 0px", threshold: 0.01 },
          )
        : null;
    if (el && observer) observer.observe(el);

    return () => {
      cancelled = true;
      window.removeEventListener("hashchange", onHashChange);
      document.removeEventListener("pointerdown", onFirstInteraction);
      observer?.disconnect();
    };
  }, [shouldLoad]);

  if (shouldLoad) {
    createBotEverMountedRef.current = true;
  }

  const showCreateBot = shouldLoad || createBotEverMountedRef.current;

  return (
    <div ref={containerRef} id="create-bot">
      {showCreateBot ? (
        <CreateBot
          {...props}
          afterEngines={afterEngines}
          afterStep2={afterStep2}
        />
      ) : (
        <>
          <CreateBotPlaceholder />
          {afterEngines ? (
            <div className="sr-only">{afterEngines}</div>
          ) : null}
        </>
      )}
    </div>
  );
}
